import React, { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import AgoraRTC from 'agora-rtc-sdk-ng'
import VirtualBackgroundExtension from 'agora-extension-virtual-background'
import useVideocall from 'hooks/useVideoCall'
import { suppressAnalyticsErrors } from 'configs/agoraClient'
import { useTransactionalLog } from 'services/transactionalLogs'
import { callEventName, buildCallPayload } from './callLogBuilders'
import * as S from './styles'

import image1 from '../../assets/backgrounds/image1.jpg'
import image2 from '../../assets/backgrounds/image2.jpg'
import image3 from '../../assets/backgrounds/image3.jpg'
import image4 from '../../assets/backgrounds/image4.jpg'
import disableIcon from '../../assets/backgrounds/disable.svg'
import unfocusedIcon from '../../assets/backgrounds/unfocused.svg'

interface VideoCallProps {
  onCloseRoom: () => void
  token: string
  schedulingId: string
  showPermissions?: { camera: boolean; microphone: boolean }
}

const VideoCall: React.FC<VideoCallProps> = ({
  onCloseRoom,
  token,
  schedulingId,
  showPermissions = { camera: true, microphone: true },
}) => {
  const { sendLog } = useTransactionalLog()
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const correlationIdRef = useRef<string | null>(null)
  const previousConnectionStatusRef = useRef<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [activeNetworkMessage, setActiveNetworkMessage] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)

  const getCallLogContext = () => ({
    appointmentId: schedulingId || '',
    correlationId:
      correlationIdRef.current || `corr_${Date.now()}_${schedulingId}`,
  })

  const applyVideoObjectFit = (
    videoEl: HTMLVideoElement,
    kind: 'remote' | 'local'
  ) => {
    const apply = () => {
      const w = videoEl.videoWidth
      const h = videoEl.videoHeight
      if (!w || !h) return
      const fit =
        kind === 'remote' && isFullscreen
          ? 'contain'
          : h > w
            ? 'contain'
            : 'cover'
      videoEl.style.setProperty('object-fit', fit, 'important')
      videoEl.style.setProperty('object-position', 'center', 'important')
      videoEl.style.setProperty('background-color', '#000', 'important')
    }

    apply()

    const anyVideo = videoEl as any
    if (anyVideo.__starbemFitBound) return
    anyVideo.__starbemFitBound = true

    videoEl.addEventListener('loadedmetadata', apply)

    if (typeof anyVideo.requestVideoFrameCallback === 'function') {
      let lastW = 0
      let lastH = 0
      const tick = () => {
        if (!videoEl.isConnected) return
        const cw = videoEl.videoWidth
        const ch = videoEl.videoHeight
        if (cw && ch && (cw !== lastW || ch !== lastH)) {
          lastW = cw
          lastH = ch
          apply()
        }
        anyVideo.requestVideoFrameCallback(tick)
      }
      anyVideo.requestVideoFrameCallback(tick)
    }
  }

  const {
    agoraClient,
    localParameters,
    initAgora,
    join,
    leaveRoom,
    remotePaused,
    remoteDisconnected,
    remoteUsers,
    toggleAudio,
    toggleVideo,
    setBackgroundBlurring,
    setBackgroundImage,
    setBackgroundNone,
    buttonsDisabled,
    networkQuality,
    networkMessage,
    connectionStatus,
    checkConnectionQuality,
    showDeviceConfig,
    setShowDeviceConfig,
  } = useVideocall()

  const extension = new VirtualBackgroundExtension()
  AgoraRTC.registerExtensions([extension])

  useEffect(() => {
    suppressAnalyticsErrors()
  }, [])

  useEffect(() => {
    if (!extension.checkCompatibility()) {
      console.error('Does not support Virtual Background!')
    }
  }, [extension])

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play().catch((error) => {
            console.error('Error ao iniciar o video: ', error.message)
          })
        }
      }
    } catch (error: any) {
      setError(error.message || 'Erro ao acessar a câmera')
    }
  }

  const stopCamera = () => {
    if (agoraClient) {
      agoraClient.removeAllListeners()
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        if (track.readyState !== 'ended') {
          track.stop()
        }
      })
      streamRef.current = null
    }
    if (videoRef.current) {
      if (videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream
        stream.getTracks().forEach((track) => {
          if (track.readyState !== 'ended') {
            track.stop()
          }
        })
        videoRef.current.srcObject = null
      }
      videoRef.current.pause()
    }
    const allVideos = document.querySelectorAll('video')
    allVideos.forEach((video) => {
      if (video instanceof HTMLVideoElement && video.srcObject) {
        const stream = video.srcObject as MediaStream
        stream.getTracks().forEach((track) => {
          if (track.kind === 'video' && track.readyState !== 'ended') {
            track.stop()
          }
        })
        video.srcObject = null
        video.pause()
      }
    })
  }

  useEffect(() => {
    if (showPermissions.camera && showPermissions.microphone && error) {
      setError(null)
    }
  }, [showPermissions.camera, showPermissions.microphone, error])

  useEffect(() => {
    if (token && showPermissions.camera && showPermissions.microphone) {
      initAgora()
    }
    return () => {
      stopCamera()
    }
  }, [token, showPermissions.camera, showPermissions.microphone])

  useEffect(() => {
    if (!token && showPermissions.camera) {
      setError(null)
      startCamera()
    }
  }, [token, showPermissions.camera])

  useEffect(() => {
    let isMounted = true

    if (
      schedulingId &&
      token &&
      showPermissions.camera &&
      showPermissions.microphone
    ) {
      setError(null)

      const remote = document.getElementById('remote')
      const local = document.getElementById('local')
      if (remote) {
        const remoteVideos = remote.querySelectorAll('video')
        remoteVideos.forEach((video) => {
          if (video instanceof HTMLVideoElement) {
            video.pause()
            video.srcObject = null
            if (video.parentNode) {
              video.remove()
            }
          }
        })
      }
      if (local) {
        const localVideos = local.querySelectorAll('video')
        localVideos.forEach((video) => {
          if (video instanceof HTMLVideoElement) {
            video.pause()
            video.srcObject = null
            if (video.parentNode) {
              video.remove()
            }
          }
        })
      }

      correlationIdRef.current = `corr_${Date.now()}_${schedulingId}`
      const ctx = getCallLogContext()
      sendLog(
        callEventName('join.requested'),
        buildCallPayload('join.requested', ctx)
      )

      join(schedulingId, token)
        .then(() => {
          if (!isMounted) return
          sendLog(
            callEventName('join.succeeded'),
            buildCallPayload('join.succeeded', getCallLogContext())
          )
        })
        .catch((error: any) => {
          if (!isMounted) return

          console.error('[VideoCall] Erro ao entrar na chamada:', error)
          const errorMessage =
            error.message || 'Erro ao acessar câmera/microfone'
          setError(errorMessage)

          const errorCode =
            error?.code === 'PERMISSION_DENIED' ||
            error?.name === 'NotAllowedError'
              ? 'PERMISSION_DENIED'
              : error?.message?.includes('Token') ||
                  error?.message?.includes('token')
                ? 'AGORA_TOKEN_EXPIRED'
                : 'CALL_JOIN_FAILED'
          sendLog(
            callEventName('join.failed'),
            buildCallPayload('join.failed', getCallLogContext(), {
              errorCode,
              errorMessage,
              httpStatus: error?.response?.status || 500,
            })
          )

          if (
            errorMessage.includes('Permissão') ||
            errorMessage.includes('permita')
          ) {
            setError(errorMessage)
          }
        })
    }

    return () => {
      isMounted = false

      const remote = document.getElementById('remote')
      const local = document.getElementById('local')
      if (remote) {
        const remoteVideos = remote.querySelectorAll('video')
        remoteVideos.forEach((video) => {
          if (video instanceof HTMLVideoElement) {
            video.pause()
            video.srcObject = null
            if (video.parentNode) {
              video.remove()
            }
          }
        })
      }
      if (local) {
        const localVideos = local.querySelectorAll('video')
        localVideos.forEach((video) => {
          if (video instanceof HTMLVideoElement) {
            video.pause()
            video.srcObject = null
            if (video.parentNode) {
              video.remove()
            }
          }
        })
      }

      setTimeout(() => {
        if (agoraClient && agoraClient.connectionState !== 'DISCONNECTED') {
          leaveRoom().catch((error) => {
            console.warn('Erro ao limpar conexão no Fast Refresh:', error)
          })
        }
      }, 0)
    }
  }, [schedulingId, token, showPermissions.camera, showPermissions.microphone])

  const toggleFullscreen = () => {
    const newFullscreen = !isFullscreen
    setIsFullscreen(newFullscreen)
    if (schedulingId && correlationIdRef.current) {
      sendLog(
        callEventName('fullscreen.toggled'),
        buildCallPayload('fullscreen.toggled', getCallLogContext(), {
          fullscreen: newFullscreen,
        })
      )
    }
  }

  const leave = async () => {
    try {
      if (correlationIdRef.current && schedulingId) {
        sendLog(
          callEventName('leave'),
          buildCallPayload('leave', getCallLogContext())
        )
      }
      if (isFullscreen) {
        setIsFullscreen(false)
      }

      stopCamera()

      const local = document.getElementById('local')
      if (local) {
        const allVideos = local.querySelectorAll('video')
        allVideos.forEach((video) => {
          if (video instanceof HTMLVideoElement) {
            if (video.srcObject) {
              const stream = video.srcObject as MediaStream
              stream.getTracks().forEach((track) => {
                if (track.readyState !== 'ended') {
                  track.stop()
                }
              })
              video.srcObject = null
            }
            video.pause()
            if (video.parentNode) {
              video.remove()
            }
          }
        })
      }

      await new Promise((resolve) => setTimeout(resolve, 100))

      await leaveRoom()
      localStorage.removeItem('callToken')
      localStorage.removeItem('callAppointmentId')

      stopCamera()

      onCloseRoom()
    } catch (error) {
      console.error('Erro ao encerrar chamada:', error)
      stopCamera()
      onCloseRoom()
      localStorage.removeItem('callToken')
      localStorage.removeItem('callAppointmentId')
    }
  }

  useEffect(() => {
    setTimeout(() => {
      checkConnectionQuality()
    }, 100)
  }, [token])

  useEffect(() => {
    if (activeNetworkMessage) {
      setTimeout(() => {
        setActiveNetworkMessage(!activeNetworkMessage)
      }, 5000)
    }
  }, [activeNetworkMessage])

  useEffect(() => {
    if (networkMessage) {
      setActiveNetworkMessage(true)
    }
  }, [networkMessage])

  // Log de instabilidade de rede durante a consulta (degradação e recuperação)
  useEffect(() => {
    if (
      !token ||
      !schedulingId ||
      !correlationIdRef.current ||
      connectionStatus === previousConnectionStatusRef.current
    ) {
      previousConnectionStatusRef.current = connectionStatus
      return
    }

    const badStatuses = ['poor', 'critical', 'disconnected']
    const goodStatuses = ['excellent', 'good']
    const prev = previousConnectionStatusRef.current
    const isNowBad = badStatuses.includes(connectionStatus)
    const wasBad = prev !== null && badStatuses.includes(prev)
    const isNowGood = goodStatuses.includes(connectionStatus)
    const wasDegradedDuringCall = prev === 'poor' || prev === 'critical' // recuperação só conta se tinha degradado (não "disconnected" inicial)

    const ctx = getCallLogContext()
    const browserQuality = (
      networkQuality as {
        browserQuality?: {
          effectiveType?: string
          downlink?: number
          rtt?: number
        }
      }
    )?.browserQuality

    if (isNowBad && (prev === null || goodStatuses.includes(prev))) {
      sendLog(
        callEventName('connection.quality.degraded'),
        buildCallPayload('connection.quality.degraded', ctx, {
          connectionStatus,
          previousConnectionStatus: prev ?? undefined,
          networkMessage,
          txQuality: networkQuality.txQuality,
          rxQuality: networkQuality.rxQuality,
          overallQuality: networkQuality.overallQuality,
          browserEffectiveType: browserQuality?.effectiveType,
          browserDownlink: browserQuality?.downlink,
          browserRtt: browserQuality?.rtt,
        })
      )
    } else if (isNowGood && wasDegradedDuringCall) {
      sendLog(
        callEventName('connection.quality.recovered'),
        buildCallPayload('connection.quality.recovered', ctx, {
          connectionStatus,
          previousConnectionStatus: prev ?? undefined,
          networkMessage,
          txQuality: networkQuality.txQuality,
          rxQuality: networkQuality.rxQuality,
          overallQuality: networkQuality.overallQuality,
          browserEffectiveType: browserQuality?.effectiveType,
          browserDownlink: browserQuality?.downlink,
          browserRtt: browserQuality?.rtt,
        })
      )
    }

    previousConnectionStatusRef.current = connectionStatus
  }, [
    token,
    schedulingId,
    connectionStatus,
    networkMessage,
    networkQuality,
    sendLog,
  ])

  useEffect(() => {
    if (token && localParameters.videoTrack && localParameters.videoEnabled) {
      const local = document.getElementById('local')
      const videoTrack = localParameters.videoTrack
      if (local && videoTrack) {
        const delay = isFullscreen ? 500 : 300
        const timeoutId = setTimeout(() => {
          if (!localParameters.videoTrack) return

          const localVideo = local.querySelector(
            'video[data-track-type="local"]'
          )

          if (localVideo instanceof HTMLVideoElement) {
            try {
              if (localVideo.paused || !localVideo.srcObject) {
                if (localParameters.videoTrack) {
                  try {
                    localParameters.videoTrack.play(localVideo)
                    applyVideoObjectFit(localVideo, 'local')
                  } catch (error) {
                    console.warn(
                      '[VideoCall] Erro ao reproduzir vídeo, tentando novamente:',
                      error
                    )
                    setTimeout(() => {
                      if (localParameters.videoTrack) {
                        try {
                          localParameters.videoTrack.play(localVideo)
                          applyVideoObjectFit(localVideo, 'local')
                        } catch (retryError) {
                          console.error(
                            '[VideoCall] Erro ao reproduzir após retry:',
                            retryError
                          )
                        }
                      }
                    }, 100)
                  }
                }
              }
            } catch (error) {
              console.warn(
                '[VideoCall] Erro ao verificar/reproduzir vídeo:',
                error
              )
              if (localParameters.videoTrack) {
                try {
                  localParameters.videoTrack.play(localVideo)
                  applyVideoObjectFit(localVideo, 'local')
                } catch (retryError) {
                  console.error(
                    '[VideoCall] Erro ao reproduzir vídeo local:',
                    retryError
                  )
                }
              }
            }
          } else {
            const videoElement = document.createElement('video')
            videoElement.setAttribute('data-track-type', 'local')
            videoElement.style.width = '100%'
            videoElement.style.height = '100%'
            videoElement.style.objectFit = 'cover'
            videoElement.style.display = 'block'
            local.appendChild(videoElement)
            setTimeout(() => {
              if (localParameters.videoTrack) {
                try {
                  localParameters.videoTrack.play(videoElement)
                  applyVideoObjectFit(videoElement, 'local')
                } catch (playError) {
                  console.error(
                    '[VideoCall] Erro ao reproduzir vídeo local:',
                    playError
                  )
                }
              }
            }, 50)
          }
        }, delay)

        return () => clearTimeout(timeoutId)
      }
    }
  }, [
    token,
    localParameters.videoTrack,
    localParameters.videoEnabled,
    isFullscreen,
  ])

  useEffect(() => {
    if (!token) return

    const timeoutId = setTimeout(() => {
      if (localParameters.videoTrack && localParameters.videoEnabled) {
        const local = document.getElementById('local')
        if (local) {
          const existingVideos = local.querySelectorAll('video')
          existingVideos.forEach((video) => {
            if (video instanceof HTMLVideoElement) {
              video.pause()
              video.srcObject = null
              if (video.parentNode) {
                video.remove()
              }
            }
          })

          const videoElement = document.createElement('video')
          videoElement.setAttribute('data-track-type', 'local')
          videoElement.style.width = '100%'
          videoElement.style.height = '100%'
          videoElement.style.objectFit = 'cover'
          videoElement.style.display = 'block'
          local.appendChild(videoElement)

          setTimeout(() => {
            if (localParameters.videoTrack) {
              try {
                localParameters.videoTrack.play(videoElement)
                applyVideoObjectFit(videoElement, 'local')
              } catch (error) {
                console.error(
                  '[VideoCall] Erro ao reproduzir vídeo local no fullscreen:',
                  error
                )
              }
            }
          }, 100)
        }
      }

      if (remoteUsers.size > 0) {
        const remote = document.getElementById('remote')
        if (remote) {
          const existingGrid = remote.querySelector(
            '.remote-videos-grid'
          ) as HTMLDivElement

          const currentUserIds = Array.from(remoteUsers.keys()).sort()
          const existingUserIds =
            existingGrid && existingGrid.parentNode === remote
              ? Array.from(existingGrid.querySelectorAll('.video-wrapper'))
                  .map((el) => el.getAttribute('data-user-uid'))
                  .filter(Boolean)
                  .sort()
              : []

          const userCountChanged =
            currentUserIds.length !== existingUserIds.length
          const userIdsChanged =
            JSON.stringify(currentUserIds) !== JSON.stringify(existingUserIds)

          if (existingGrid && !userCountChanged && !userIdsChanged) {
            const sortedUsers = Array.from(remoteUsers.values()).sort(
              (a, b) => {
                const uidA = String(a.uid)
                const uidB = String(b.uid)
                return uidA.localeCompare(uidB)
              }
            )

            sortedUsers.forEach((user) => {
              const existingWrapper = existingGrid.querySelector(
                `.video-wrapper[data-user-uid="${user.uid}"]`
              ) as HTMLElement

              if (existingWrapper) {
                const isPlaceholder =
                  existingWrapper.getAttribute('data-placeholder') === 'true'
                const shouldBePlaceholder =
                  user.videoPaused || !user.hasVideo || !user.videoTrack

                if (isPlaceholder === shouldBePlaceholder) {
                  if (!shouldBePlaceholder && user.videoTrack) {
                    const videoElement = existingWrapper.querySelector(
                      'video[data-user-uid]'
                    ) as HTMLVideoElement
                    if (videoElement && user.videoTrack) {
                      try {
                        user.videoTrack
                          .play(videoElement)
                          .then(() =>
                            applyVideoObjectFit(videoElement, 'remote')
                          )
                          .catch(() => {})
                      } catch (error) {
                        // Ignora erros de play
                      }
                    }
                  }
                  return
                }

                const allWrappers = Array.from(
                  existingGrid.querySelectorAll('.video-wrapper')
                )
                const currentIndex = allWrappers.findIndex(
                  (w) => w.getAttribute('data-user-uid') === String(user.uid)
                )
                const referenceWrapper =
                  currentIndex > 0 ? allWrappers[currentIndex - 1] : null

                if (
                  existingWrapper &&
                  existingWrapper.parentNode === existingGrid
                ) {
                  existingWrapper.remove()
                }

                let newWrapper: HTMLElement | null = null

                if (user.videoTrack && user.hasVideo && !user.videoPaused) {
                  const videoWrapper = document.createElement('div')
                  videoWrapper.className = 'video-wrapper'
                  videoWrapper.setAttribute('data-user-uid', String(user.uid))
                  videoWrapper.style.width = '100%'
                  videoWrapper.style.height = '100%'
                  videoWrapper.style.position = 'relative'
                  videoWrapper.style.backgroundColor = '#000'
                  videoWrapper.style.borderRadius = '8px'
                  videoWrapper.style.overflow = 'hidden'
                  videoWrapper.style.display = 'flex'
                  videoWrapper.style.alignItems = 'center'
                  videoWrapper.style.justifyContent = 'center'
                  videoWrapper.style.minWidth = '0'
                  videoWrapper.style.minHeight = '0'

                  const videoElement = document.createElement('video')
                  videoElement.setAttribute('data-user-uid', String(user.uid))
                  videoElement.style.width = '100%'
                  videoElement.style.height = '100%'
                  videoElement.style.objectFit = 'cover'
                  videoElement.style.display = 'block'
                  videoElement.style.minWidth = '0'
                  videoElement.style.minHeight = '0'

                  videoWrapper.appendChild(videoElement)
                  newWrapper = videoWrapper

                  setTimeout(() => {
                    try {
                      user.videoTrack.play(videoElement)
                      applyVideoObjectFit(videoElement, 'remote')
                    } catch (error) {
                      console.error(
                        `[VideoCall] Erro ao reproduzir vídeo remoto ${user.uid}:`,
                        error
                      )
                    }
                  }, 50)
                } else {
                  const placeholderWrapper = document.createElement('div')
                  placeholderWrapper.className = 'video-wrapper'
                  placeholderWrapper.setAttribute(
                    'data-user-uid',
                    String(user.uid)
                  )
                  placeholderWrapper.setAttribute('data-placeholder', 'true')
                  placeholderWrapper.style.width = '100%'
                  placeholderWrapper.style.height = '100%'
                  placeholderWrapper.style.position = 'relative'
                  placeholderWrapper.style.background =
                    'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
                  placeholderWrapper.style.borderRadius = '8px'
                  placeholderWrapper.style.overflow = 'hidden'
                  placeholderWrapper.style.display = 'flex'
                  placeholderWrapper.style.alignItems = 'center'
                  placeholderWrapper.style.justifyContent = 'center'
                  placeholderWrapper.style.flexDirection = 'column'
                  placeholderWrapper.style.gap = '12px'

                  const contentDiv = document.createElement('div')
                  contentDiv.style.display = 'flex'
                  contentDiv.style.flexDirection = 'column'
                  contentDiv.style.alignItems = 'center'
                  contentDiv.style.justifyContent = 'center'
                  contentDiv.style.gap = '12px'

                  const iconSvg = document.createElementNS(
                    'http://www.w3.org/2000/svg',
                    'svg'
                  )
                  iconSvg.setAttribute('width', '60')
                  iconSvg.setAttribute('height', '60')
                  iconSvg.setAttribute('viewBox', '0 0 24 24')
                  iconSvg.setAttribute('fill', 'none')
                  iconSvg.style.color = '#EA7330'
                  iconSvg.style.opacity = '0.6'

                  const path1 = document.createElementNS(
                    'http://www.w3.org/2000/svg',
                    'path'
                  )
                  path1.setAttribute(
                    'd',
                    'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z'
                  )
                  path1.setAttribute('stroke', 'currentColor')
                  path1.setAttribute('stroke-width', '2')
                  path1.setAttribute('stroke-linecap', 'round')
                  path1.setAttribute('stroke-linejoin', 'round')

                  const path2 = document.createElementNS(
                    'http://www.w3.org/2000/svg',
                    'path'
                  )
                  path2.setAttribute('d', 'M3 3l18 18')
                  path2.setAttribute('stroke', 'currentColor')
                  path2.setAttribute('stroke-width', '2')
                  path2.setAttribute('stroke-linecap', 'round')

                  iconSvg.appendChild(path1)
                  iconSvg.appendChild(path2)

                  const text = document.createElement('span')
                  text.textContent = 'Câmera desativada'
                  text.style.color = '#FFFFFF'
                  text.style.fontSize = '14px'
                  text.style.fontWeight = '500'
                  text.style.textAlign = 'center'
                  text.style.opacity = '0.8'

                  contentDiv.appendChild(iconSvg)
                  contentDiv.appendChild(text)
                  placeholderWrapper.appendChild(contentDiv)
                  newWrapper = placeholderWrapper
                }

                if (newWrapper) {
                  if (referenceWrapper && referenceWrapper.nextSibling) {
                    existingGrid.insertBefore(
                      newWrapper,
                      referenceWrapper.nextSibling
                    )
                  } else if (referenceWrapper) {
                    existingGrid.appendChild(newWrapper)
                  } else {
                    const firstChild = existingGrid.firstChild
                    if (firstChild) {
                      existingGrid.insertBefore(newWrapper, firstChild)
                    } else {
                      existingGrid.appendChild(newWrapper)
                    }
                  }
                }
              }
            })

            existingGrid
              .querySelectorAll('.video-wrapper')
              .forEach((wrapper) => {
                const uid = wrapper.getAttribute('data-user-uid')
                if (
                  uid &&
                  !remoteUsers.has(uid) &&
                  !remoteUsers.has(Number(uid))
                ) {
                  if (wrapper.parentNode === existingGrid) {
                    wrapper.remove()
                  }
                }
              })

            return
          }

          const existingVideos = remote.querySelectorAll('video')
          existingVideos.forEach((video) => {
            if (video instanceof HTMLVideoElement) {
              video.pause()
              video.srcObject = null
              if (video.parentNode) {
                video.remove()
              }
            }
          })

          if (existingGrid && existingGrid.parentNode === remote) {
            existingGrid.remove()
          }

          const totalVideos = remoteUsers.size

          let columns = 1
          let rows = 1

          if (isFullscreen) {
            if (totalVideos === 1) {
              columns = 1
              rows = 1
            } else if (totalVideos === 2) {
              columns = 2
              rows = 1
            } else if (totalVideos === 3) {
              columns = 3
              rows = 1
            } else if (totalVideos === 4) {
              columns = 2
              rows = 2
            } else if (totalVideos <= 6) {
              columns = 3
              rows = 2
            } else if (totalVideos <= 9) {
              columns = 3
              rows = 3
            } else {
              columns = 4
              rows = Math.ceil(totalVideos / 4)
            }
          } else {
            if (totalVideos === 1) {
              columns = 1
              rows = 1
            } else if (totalVideos === 2) {
              columns = 1
              rows = 2
            } else if (totalVideos === 3) {
              columns = 1
              rows = 3
            } else if (totalVideos === 4) {
              columns = 2
              rows = 2
            } else if (totalVideos <= 6) {
              columns = 3
              rows = 2
            } else if (totalVideos <= 9) {
              columns = 3
              rows = 3
            } else {
              columns = Math.ceil(Math.sqrt(totalVideos))
              rows = Math.ceil(totalVideos / columns)
              while (columns * rows - totalVideos >= columns) {
                rows--
              }
            }
          }

          const gridContainer = document.createElement('div')
          gridContainer.className = 'remote-videos-grid'
          gridContainer.style.display = 'grid'
          gridContainer.style.width = '100%'
          gridContainer.style.height = '100%'
          gridContainer.style.gap = '8px'
          gridContainer.style.padding = '8px'
          gridContainer.style.overflow = 'hidden'
          gridContainer.style.boxSizing = 'border-box'
          gridContainer.style.background = '#000'
          gridContainer.style.placeItems = 'stretch'
          gridContainer.style.gridTemplateColumns = `repeat(${columns}, 1fr)`
          gridContainer.style.gridTemplateRows = `repeat(${rows}, 1fr)`
          remote.appendChild(gridContainer)

          remoteUsers.forEach((user) => {
            if (user.videoTrack && user.hasVideo && !user.videoPaused) {
              const videoWrapper = document.createElement('div')
              videoWrapper.className = 'video-wrapper'
              videoWrapper.setAttribute('data-user-uid', String(user.uid))
              videoWrapper.style.width = '100%'
              videoWrapper.style.height = '100%'
              videoWrapper.style.position = 'relative'
              videoWrapper.style.backgroundColor = '#000'
              videoWrapper.style.borderRadius = '8px'
              videoWrapper.style.overflow = 'hidden'
              videoWrapper.style.display = 'flex'
              videoWrapper.style.alignItems = 'center'
              videoWrapper.style.justifyContent = 'center'
              videoWrapper.style.minWidth = '0'
              videoWrapper.style.minHeight = '0'

              const videoElement = document.createElement('video')
              videoElement.setAttribute('data-user-uid', String(user.uid))
              videoElement.style.width = '100%'
              videoElement.style.height = '100%'
              videoElement.style.objectFit = 'cover'
              videoElement.style.display = 'block'
              videoElement.style.minWidth = '0'
              videoElement.style.minHeight = '0'

              videoWrapper.appendChild(videoElement)
              gridContainer.appendChild(videoWrapper)

              setTimeout(() => {
                try {
                  user.videoTrack.play(videoElement)
                  applyVideoObjectFit(videoElement, 'remote')
                } catch (error) {
                  console.error(
                    `[VideoCall] Erro ao reproduzir vídeo remoto ${user.uid}:`,
                    error
                  )
                }
              }, 100)
            } else if (user.videoPaused || !user.hasVideo) {
              const placeholderWrapper = document.createElement('div')
              placeholderWrapper.className = 'video-wrapper'
              placeholderWrapper.setAttribute('data-user-uid', String(user.uid))
              placeholderWrapper.setAttribute('data-placeholder', 'true')
              placeholderWrapper.style.width = '100%'
              placeholderWrapper.style.height = '100%'
              placeholderWrapper.style.position = 'relative'
              placeholderWrapper.style.background =
                'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
              placeholderWrapper.style.borderRadius = '8px'
              placeholderWrapper.style.overflow = 'hidden'
              placeholderWrapper.style.display = 'flex'
              placeholderWrapper.style.alignItems = 'center'
              placeholderWrapper.style.justifyContent = 'center'
              placeholderWrapper.style.flexDirection = 'column'
              placeholderWrapper.style.gap = '12px'

              const contentDiv = document.createElement('div')
              contentDiv.style.display = 'flex'
              contentDiv.style.flexDirection = 'column'
              contentDiv.style.alignItems = 'center'
              contentDiv.style.justifyContent = 'center'
              contentDiv.style.gap = '12px'

              const iconSvg = document.createElementNS(
                'http://www.w3.org/2000/svg',
                'svg'
              )
              iconSvg.setAttribute('width', '60')
              iconSvg.setAttribute('height', '60')
              iconSvg.setAttribute('viewBox', '0 0 24 24')
              iconSvg.setAttribute('fill', 'none')
              iconSvg.style.color = '#EA7330'
              iconSvg.style.opacity = '0.6'

              const path1 = document.createElementNS(
                'http://www.w3.org/2000/svg',
                'path'
              )
              path1.setAttribute(
                'd',
                'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z'
              )
              path1.setAttribute('stroke', 'currentColor')
              path1.setAttribute('stroke-width', '2')
              path1.setAttribute('stroke-linecap', 'round')
              path1.setAttribute('stroke-linejoin', 'round')

              const path2 = document.createElementNS(
                'http://www.w3.org/2000/svg',
                'path'
              )
              path2.setAttribute('d', 'M3 3l18 18')
              path2.setAttribute('stroke', 'currentColor')
              path2.setAttribute('stroke-width', '2')
              path2.setAttribute('stroke-linecap', 'round')

              iconSvg.appendChild(path1)
              iconSvg.appendChild(path2)

              const text = document.createElement('span')
              text.textContent = 'Câmera desativada'
              text.style.color = '#FFFFFF'
              text.style.fontSize = '14px'
              text.style.fontWeight = '500'
              text.style.textAlign = 'center'
              text.style.opacity = '0.8'

              contentDiv.appendChild(iconSvg)
              contentDiv.appendChild(text)
              placeholderWrapper.appendChild(contentDiv)
              gridContainer.appendChild(placeholderWrapper)
            }
          })
        }
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [isFullscreen, token, localParameters.videoTrack, remoteUsers])

  const classRemote = `main ${remotePaused ? 'paused' : ''} ${remoteDisconnected ? 'disconnected' : ''}`
  const classLocal = `secondary`

  const videoCallContent = (
    <S.Container isFullscreen={isFullscreen}>
      <S.VideoContainer isFullscreen={isFullscreen}>
        {token && (
          <S.RemoteVideo id="remote" className={classRemote}>
            {error ? (
              <S.ErrorContainer>
                <S.ErrorTitle>{error}</S.ErrorTitle>
                <S.ErrorSubtitle>
                  Por favor, verifique as permissões do navegador e recarregue a
                  página.
                </S.ErrorSubtitle>
              </S.ErrorContainer>
            ) : (
              (() => {
                const hasNoUsers = remoteDisconnected || remoteUsers.size === 0

                const hasUsersButAllPaused =
                  remoteUsers.size > 0 &&
                  !remoteDisconnected &&
                  Array.from(remoteUsers.values()).every(
                    (u) => u.videoPaused || !u.hasVideo
                  )

                const remote = document.getElementById('remote')
                const hasVideosInGrid =
                  remote?.querySelector('.remote-videos-grid') !== null

                if (hasNoUsers || (hasUsersButAllPaused && !hasVideosInGrid)) {
                  const isPaused =
                    hasUsersButAllPaused ||
                    (remotePaused && !remoteDisconnected)

                  return (
                    <S.Placeholder>
                      <S.PlaceholderContent>
                        <img
                          src={
                            isPaused
                              ? '/icons/video-off.svg'
                              : '/icons/icon-avatar.svg'
                          }
                          alt={
                            isPaused
                              ? 'Câmera desativada'
                              : 'Aguardando conexão'
                          }
                          width={80}
                          height={80}
                          style={{ borderRadius: '50%', objectFit: 'cover' }}
                        />
                        <S.PlaceholderTitle>
                          {isPaused
                            ? 'Câmera desativada'
                            : 'Aguardando conexão'}
                        </S.PlaceholderTitle>
                        <S.PlaceholderSubtitle>
                          {isPaused
                            ? remoteUsers.size === 1
                              ? 'O paciente desativou a câmera dele'
                              : 'Os participantes desativaram as câmeras'
                            : 'O paciente entrará em breve na consulta'}
                        </S.PlaceholderSubtitle>
                      </S.PlaceholderContent>
                    </S.Placeholder>
                  )
                }

                return null
              })()
            )}
          </S.RemoteVideo>
        )}
        {token ? (
          <S.LocalVideo
            id="local"
            className={classLocal}
            isFullscreen={isFullscreen}
          >
            {!localParameters.videoEnabled ? (
              <S.Placeholder>
                <S.PlaceholderContent isLocal>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    style={{ color: '#EA7330', opacity: 0.6 }}
                  >
                    <path
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M3 3l18 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </S.PlaceholderContent>
              </S.Placeholder>
            ) : null}
          </S.LocalVideo>
        ) : (
          <>
            <video ref={videoRef} style={{ width: '100%', height: '100%' }} />
            <S.CameraTest
              error={
                Boolean(error) ||
                (!showPermissions.camera && !showPermissions.microphone)
              }
              warning={!showPermissions.camera || !showPermissions.microphone}
            >
              {showPermissions.camera && showPermissions.microphone
                ? `Está tudo certo com sua câmera e microfone, clique no botão "Entrar na chamada" para começar!`
                : showPermissions.camera && !showPermissions.microphone
                  ? `Está tudo certo com sua câmera, falta permitir o acesso ao microfone`
                  : !showPermissions.camera && showPermissions.microphone
                    ? `Está tudo certo com seu microfone, falta permitir o acesso à câmera`
                    : `É necessário permitir o acesso à câmera e ao microfone para iniciar a chamada. Clique no botão "Solicitar permissões" para continuar.`}
            </S.CameraTest>
          </>
        )}
        {token && activeNetworkMessage && (
          <S.NetworkMessage
            isPoor={
              connectionStatus === 'critical' ||
              connectionStatus === 'disconnected'
            }
            isUnstable={connectionStatus === 'poor'}
            isRisky={connectionStatus === 'good'}
            overallQuality={networkQuality.overallQuality}
            connectionStatus={connectionStatus}
          >
            <S.NetworkBars>
              {[1, 2, 3].map((bar: number) => {
                let numBars = 3
                if (
                  connectionStatus === 'disconnected' ||
                  connectionStatus === 'critical'
                ) {
                  numBars = 1
                } else if (connectionStatus === 'poor') {
                  numBars = 1
                } else if (connectionStatus === 'good') {
                  numBars = 2
                } else if (connectionStatus === 'excellent') {
                  numBars = 3
                }

                const isActive = numBars >= bar
                const isPoor =
                  connectionStatus === 'critical' ||
                  connectionStatus === 'disconnected'
                const isUnstable = connectionStatus === 'poor'
                const isRisky = connectionStatus === 'good'

                return (
                  <S.NetworkBar
                    key={bar}
                    isActive={isActive}
                    barNumber={bar}
                    isPoor={isPoor}
                    isUnstable={isUnstable}
                    isRisky={isRisky}
                  />
                )
              })}
            </S.NetworkBars>
            <span>{networkMessage}</span>
          </S.NetworkMessage>
        )}
      </S.VideoContainer>
      {token && (
        <>
          {isFullscreen && showDeviceConfig && (
            <S.BackgroundButtons isFullscreen={isFullscreen}>
              <S.BackgroundButton
                isFullscreen={isFullscreen}
                onClick={() => {
                  setBackgroundNone()
                  if (schedulingId && correlationIdRef.current) {
                    sendLog(
                      callEventName('background.changed'),
                      buildCallPayload(
                        'background.changed',
                        getCallLogContext(),
                        {
                          backgroundType: 'none',
                        }
                      )
                    )
                  }
                }}
              >
                <img src={disableIcon} alt="Desativar Fundo" />
              </S.BackgroundButton>
              <S.BackgroundButton
                isFullscreen={isFullscreen}
                onClick={() => {
                  setBackgroundBlurring()
                  if (schedulingId && correlationIdRef.current) {
                    sendLog(
                      callEventName('background.changed'),
                      buildCallPayload(
                        'background.changed',
                        getCallLogContext(),
                        {
                          backgroundType: 'blur',
                        }
                      )
                    )
                  }
                }}
              >
                <img src={unfocusedIcon} alt="Desfocar Fundo" />
              </S.BackgroundButton>
              <S.BackgroundButton
                isFullscreen={isFullscreen}
                onClick={() => {
                  setBackgroundImage(image1)
                  if (schedulingId && correlationIdRef.current) {
                    sendLog(
                      callEventName('background.changed'),
                      buildCallPayload(
                        'background.changed',
                        getCallLogContext(),
                        {
                          backgroundType: 'image',
                        }
                      )
                    )
                  }
                }}
              >
                <img src={image1} alt="Fundo 1" />
              </S.BackgroundButton>
              <S.BackgroundButton
                isFullscreen={isFullscreen}
                onClick={() => {
                  setBackgroundImage(image2)
                  if (schedulingId && correlationIdRef.current) {
                    sendLog(
                      callEventName('background.changed'),
                      buildCallPayload(
                        'background.changed',
                        getCallLogContext(),
                        {
                          backgroundType: 'image',
                        }
                      )
                    )
                  }
                }}
              >
                <img src={image2} alt="Fundo 2" />
              </S.BackgroundButton>
              <S.BackgroundButton
                isFullscreen={isFullscreen}
                onClick={() => {
                  setBackgroundImage(image3)
                  if (schedulingId && correlationIdRef.current) {
                    sendLog(
                      callEventName('background.changed'),
                      buildCallPayload(
                        'background.changed',
                        getCallLogContext(),
                        {
                          backgroundType: 'image',
                        }
                      )
                    )
                  }
                }}
              >
                <img src={image3} alt="Fundo 3" />
              </S.BackgroundButton>
              <S.BackgroundButton
                isFullscreen={isFullscreen}
                onClick={() => {
                  setBackgroundImage(image4)
                  if (schedulingId && correlationIdRef.current) {
                    sendLog(
                      callEventName('background.changed'),
                      buildCallPayload(
                        'background.changed',
                        getCallLogContext(),
                        {
                          backgroundType: 'image',
                        }
                      )
                    )
                  }
                }}
              >
                <img src={image4} alt="Fundo 4" />
              </S.BackgroundButton>
            </S.BackgroundButtons>
          )}
          <S.ButtonsContainer isFullscreen={isFullscreen}>
            <S.NetworkQualityButton
              onClick={() => {
                setActiveNetworkMessage(!activeNetworkMessage)
              }}
              isPoor={
                connectionStatus === 'critical' ||
                connectionStatus === 'disconnected'
              }
              isUnstable={connectionStatus === 'poor'}
            >
              <S.NetworkBars>
                {[1, 2, 3].map((bar) => {
                  let numBars = 3
                  if (
                    connectionStatus === 'disconnected' ||
                    connectionStatus === 'critical'
                  ) {
                    numBars = 1
                  } else if (connectionStatus === 'poor') {
                    numBars = 1
                  } else if (connectionStatus === 'good') {
                    numBars = 2
                  } else if (connectionStatus === 'excellent') {
                    numBars = 3
                  }

                  const isActive = numBars >= bar
                  const isPoor =
                    connectionStatus === 'critical' ||
                    connectionStatus === 'disconnected'
                  const isUnstable = connectionStatus === 'poor'
                  const isRisky = connectionStatus === 'good'

                  return (
                    <S.NetworkBar
                      key={bar}
                      isActive={isActive}
                      barNumber={bar}
                      isPoor={isPoor}
                      isUnstable={isUnstable}
                      isRisky={isRisky}
                    />
                  )
                })}
              </S.NetworkBars>
            </S.NetworkQualityButton>
            <S.FullscreenButton
              onClick={toggleFullscreen}
              active={isFullscreen}
              title={
                isFullscreen ? 'Sair do modo tela cheia' : 'Modo tela cheia'
              }
            >
              <img
                src={
                  isFullscreen
                    ? '/icons/exit-screen.svg'
                    : '/icons/full-screen.svg'
                }
                alt={isFullscreen ? 'Sair do fullscreen' : 'Fullscreen'}
              />
            </S.FullscreenButton>
            <S.SettingsButton
              onClick={() => {
                setShowDeviceConfig(!showDeviceConfig)
              }}
              active={showDeviceConfig}
              title="Alterar plano de fundo"
            >
              <img src="/icons/effects.svg" alt="Settings" />
            </S.SettingsButton>
            <S.ToggleButton
              onClick={() => {
                const newEnabled = !localParameters.videoEnabled
                toggleVideo()
                if (schedulingId && correlationIdRef.current) {
                  sendLog(
                    callEventName('video.toggled'),
                    buildCallPayload('video.toggled', getCallLogContext(), {
                      enabled: newEnabled,
                    })
                  )
                }
              }}
              active={!localParameters.videoEnabled}
              disabled={buttonsDisabled}
              title={
                localParameters.videoEnabled ? 'Parar Vídeo' : 'Iniciar Vídeo'
              }
            >
              <img
                src={
                  localParameters.videoEnabled
                    ? '/icons/video.svg'
                    : '/icons/video-off.svg'
                }
                alt="Video"
              />
            </S.ToggleButton>
            <S.EndButton
              onClick={leave}
              disabled={buttonsDisabled}
              title="Sair da chamada"
            >
              <img
                src="/icons/end-call.svg"
                alt="Hangup"
                width={46}
                height={46}
              />
            </S.EndButton>
            <S.ToggleButton
              onClick={() => {
                const newMuted = !localParameters.audioMuted
                toggleAudio()
                if (schedulingId && correlationIdRef.current) {
                  sendLog(
                    callEventName('microphone.toggled'),
                    buildCallPayload(
                      'microphone.toggled',
                      getCallLogContext(),
                      {
                        muted: newMuted,
                      }
                    )
                  )
                }
              }}
              active={localParameters.audioMuted}
              disabled={buttonsDisabled}
              title={
                localParameters.audioMuted ? 'Ativar Áudio' : 'Desativar Áudio'
              }
            >
              <img
                src={
                  localParameters.audioMuted
                    ? '/icons/microphone-off.svg'
                    : '/icons/microphone.svg'
                }
                alt="Audio"
              />
            </S.ToggleButton>
          </S.ButtonsContainer>
          {!isFullscreen && showDeviceConfig && (
            <S.BackgroundButtons isFullscreen={isFullscreen}>
              <S.BackgroundButton
                isFullscreen={isFullscreen}
                onClick={() => {
                  setBackgroundNone()
                  if (schedulingId && correlationIdRef.current) {
                    sendLog(
                      callEventName('background.changed'),
                      buildCallPayload(
                        'background.changed',
                        getCallLogContext(),
                        {
                          backgroundType: 'none',
                        }
                      )
                    )
                  }
                }}
              >
                <img src={disableIcon} alt="Desativar Fundo" />
              </S.BackgroundButton>
              <S.BackgroundButton
                isFullscreen={isFullscreen}
                onClick={() => {
                  setBackgroundBlurring()
                  if (schedulingId && correlationIdRef.current) {
                    sendLog(
                      callEventName('background.changed'),
                      buildCallPayload(
                        'background.changed',
                        getCallLogContext(),
                        {
                          backgroundType: 'blur',
                        }
                      )
                    )
                  }
                }}
              >
                <img src={unfocusedIcon} alt="Desfocar Fundo" />
              </S.BackgroundButton>
              <S.BackgroundButton
                isFullscreen={isFullscreen}
                onClick={() => {
                  setBackgroundImage(image1)
                  if (schedulingId && correlationIdRef.current) {
                    sendLog(
                      callEventName('background.changed'),
                      buildCallPayload(
                        'background.changed',
                        getCallLogContext(),
                        {
                          backgroundType: 'image',
                        }
                      )
                    )
                  }
                }}
              >
                <img src={image1} alt="Fundo 1" />
              </S.BackgroundButton>
              <S.BackgroundButton
                isFullscreen={isFullscreen}
                onClick={() => {
                  setBackgroundImage(image2)
                  if (schedulingId && correlationIdRef.current) {
                    sendLog(
                      callEventName('background.changed'),
                      buildCallPayload(
                        'background.changed',
                        getCallLogContext(),
                        {
                          backgroundType: 'image',
                        }
                      )
                    )
                  }
                }}
              >
                <img src={image2} alt="Fundo 2" />
              </S.BackgroundButton>
              <S.BackgroundButton
                isFullscreen={isFullscreen}
                onClick={() => {
                  setBackgroundImage(image3)
                  if (schedulingId && correlationIdRef.current) {
                    sendLog(
                      callEventName('background.changed'),
                      buildCallPayload(
                        'background.changed',
                        getCallLogContext(),
                        {
                          backgroundType: 'image',
                        }
                      )
                    )
                  }
                }}
              >
                <img src={image3} alt="Fundo 3" />
              </S.BackgroundButton>
              <S.BackgroundButton
                isFullscreen={isFullscreen}
                onClick={() => {
                  setBackgroundImage(image4)
                  if (schedulingId && correlationIdRef.current) {
                    sendLog(
                      callEventName('background.changed'),
                      buildCallPayload(
                        'background.changed',
                        getCallLogContext(),
                        {
                          backgroundType: 'image',
                        }
                      )
                    )
                  }
                }}
              >
                <img src={image4} alt="Fundo 4" />
              </S.BackgroundButton>
            </S.BackgroundButtons>
          )}
        </>
      )}
    </S.Container>
  )

  if (isFullscreen) {
    return createPortal(videoCallContent, document.body)
  }

  return videoCallContent
}

export default VideoCall
